import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import AuthorAvatar from "../components/AuthorAvatar";
import { useAuth } from "../lib/auth";
import { GraduationCap, ArrowRight, Clock, BookOpen, Sparkle } from "@phosphor-icons/react";

const LEVEL_META = {
  beginner: { label: "BEGINNER", color: "#00E676" },
  intermediate: { label: "INTERMEDIATE", color: "#22D3EE" },
  advanced: { label: "ADVANCED", color: "#FFAB00" },
};

export default function Courses() {
  const { user } = useAuth();
  const [courses, setCourses] = useState(null);
  const [enrollments, setEnrollments] = useState({});

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/courses");
      setCourses(data);
    })().catch(() => setCourses([]));
    if (user) {
      api.get("/courses/me/enrollments").then((r) => {
        const map = {};
        for (const e of r.data || []) map[e.course_slug] = e;
        setEnrollments(map);
      }).catch(() => {});
    }
  }, [user]);

  return (
    <AppShell>
      <div data-testid="courses-page">
        <div className="mb-10">
          <div className="label-mono mb-2 flex items-center gap-2" style={{ color: "#E60012" }}>
            <GraduationCap size={12} weight="fill" /> COURSES · AI-CURATED MOBILITY CURRICULA
          </div>
          <h1 className="font-display text-white mb-3" style={{ fontSize: "clamp(36px, 5vw, 56px)", fontWeight: 900, letterSpacing: "-0.035em", lineHeight: 0.98 }}>
            Master the future<br /><span style={{ color: "#E60012" }}>of mobility.</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl">
            Seven flagship courses authored by iAAM AI experts — covering smart mobility foundations,
            aerospace integration, AI agent operations, security, monetization, platform engineering,
            and hands-on labs.
          </p>
        </div>

        {courses === null ? (
          <div className="py-16 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING CURRICULUM...</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="courses-grid">
            {courses.map((c) => {
              const lvl = LEVEL_META[c.level] || { label: c.level.toUpperCase(), color: "#E60012" };
              const e = enrollments[c.slug];
              const lessonsReady = c.lesson_count > 0;
              const cover = c.cover_image ? `${process.env.REACT_APP_BACKEND_URL}${c.cover_image}` : "";
              return (
                <Link
                  key={c.id}
                  to={`/courses/${c.slug}`}
                  className="card card-hover overflow-hidden flex flex-col"
                  data-testid={`course-card-${c.slug}`}
                  style={{ borderTop: `2px solid ${lvl.color}` }}
                >
                  <div className="aspect-video bg-[#0A0A0C] relative overflow-hidden">
                    {cover ? (
                      <img src={cover} alt="" className="w-full h-full object-cover" loading="lazy"
                           onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-full h-full blueprint-grid-dense flex items-center justify-center">
                        <GraduationCap size={36} weight="light" style={{ color: lvl.color }} />
                      </div>
                    )}
                    <div className="absolute top-2 left-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border" style={{ color: lvl.color, borderColor: lvl.color }}>
                      {lvl.label}
                    </div>
                    {!lessonsReady && (
                      <div className="absolute top-2 right-2 label-mono px-2 py-1 bg-[#0A0A0C]/90 border" style={{ color: "#FFAB00", borderColor: "#FFAB00" }} data-testid={`course-status-generating-${c.slug}`}>
                        <Sparkle size={9} weight="fill" className="inline mr-1" />
                        GENERATING
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display text-white text-lg mb-2 line-clamp-2" style={{ fontWeight: 700, letterSpacing: "-0.015em" }}>
                      {c.title}
                    </h3>
                    <p className="text-sm text-zinc-400 line-clamp-3 mb-4 flex-1">{c.tagline}</p>
                    {lessonsReady && (
                      <div className="flex items-center gap-4 label-mono mb-3">
                        <span className="flex items-center gap-1"><BookOpen size={10} /> {c.lesson_count} LESSONS</span>
                        <span className="flex items-center gap-1"><Clock size={10} /> {c.duration_min} MIN</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-dashed border-[#27272A]">
                      <div className="flex items-center gap-2 min-w-0">
                        <AuthorAvatar name={c.instructor} avatarUrl={c.instructor_avatar ? `${process.env.REACT_APP_BACKEND_URL}${c.instructor_avatar}` : ""} size={22} bg={lvl.color} />
                        <span className="text-xs text-zinc-300 truncate">{c.instructor}</span>
                      </div>
                      {e ? (
                        <span className="label-mono" style={{ color: "#00E676" }}>{e.progress_pct}% DONE</span>
                      ) : (
                        <span className="label-mono flex items-center gap-1">FREE <ArrowRight size={9} weight="bold" /></span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
